import React, { useEffect, useRef, useState } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { Button } from '@duck/ui'
import { useTar } from '@/modules'
import { useCloseOnHistoryBack } from '@/hooks/useCloseOnHistoryBack'
import { ChallengeDetail } from './ChallengeDetail'
import { AssignmentDetail } from './AssignmentDetail'

export type DrawerItem =
  | { kind: 'challenge'; challenge: ChallengeService.AvailableChallenge }
  | { kind: 'assignment'; assignment: ChallengeService.AvailableAssignment }
  | { kind: 'unavailable'; authenticated: boolean }

const itemName = (item: DrawerItem): string =>
  item.kind === 'challenge'
    ? item.challenge.name
    : item.kind === 'assignment'
      ? item.assignment.name
      : ''

interface DrawerProps {
  item: DrawerItem | null
  /** The items immediately before/after the open one, so a swipe reveals the
   *  real neighbour with no gap. Null at the list bounds. */
  prevItem?: DrawerItem | null
  nextItem?: DrawerItem | null
  open: boolean
  position: number
  total: number
  onClose: () => void
  onNavigate: (direction: -1 | 1) => void
  onStart: () => void
  onLogin?: () => void
}

export const ChallengeDetailDrawer = ({ item, prevItem = null, nextItem = null, open, position, total, onClose, onNavigate, onStart, onLogin }: DrawerProps) => {
  const { t, isRtl } = useTar()
  // Retain the last item so content stays visible while the panel slides out
  // (the page sets `item` to null on close). The panel is always mounted so the
  // enter/exit transform transition actually plays.
  const [rendered, setRendered] = useState<DrawerItem | null>(item)

  useEffect(() => {
    if (item !== null) setRendered(item)
  }, [item])

  // Browser/phone Back closes the drawer instead of leaving the page, same as
  // our dialogs.
  const releaseHistorySentinel = useCloseOnHistoryBack(open, onClose)

  // Main CTA (Start/Continue): pop our sentinel entry first, then navigate —
  // otherwise the workspace lands on top of it and Back needs two presses.
  const handleStart = () => {
    void releaseHistorySentinel().then(onStart)
  }

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') animateNavigateRef.current(-1)
      if (e.key === 'ArrowRight') animateNavigateRef.current(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Lock background scroll while the drawer is open so a mobile overscroll can't
  // peek the page behind/below the full-screen sheet. Restored on close/unmount.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [open])

  // --- Animated navigation shared by touch swipe (mobile), trackpad horizontal
  // swipe (desktop), and the prev/next arrow buttons + keyboard. The moving
  // layer is translated by dragX; the aside keeps its own open/close transform.
  const SWIPE_THRESHOLD = 50
  const SWIPE_COMMIT_MS = 200
  const [dragX, setDragX] = useState(0)
  const [animating, setAnimating] = useState(false)
  const gesture = useRef<{ x: number; y: number; axis: 'h' | 'v' | null } | null>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const committing = useRef(false)
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => { if (commitTimer.current !== null) clearTimeout(commitTimer.current) }, [])

  // Reset any in-flight drag when the drawer closes.
  useEffect(() => { if (!open) { setDragX(0); setAnimating(false); committing.current = false } }, [open])

  // Swipe direction → navigation, mapped physically to match this drawer's
  // keyboard nav (ArrowLeft = prev, ArrowRight = next, not flipped for RTL):
  // dragging the content left (negative) advances to next, dragging right goes
  // to prev. screenSign is the direction the content moved.
  const navDirFor = (screenSign: -1 | 1): -1 | 1 => -screenSign as -1 | 1
  const canNavigate = (dir: -1 | 1) => (dir === 1 ? position < total : position > 1)
  const panelWidth = () => trackRef.current?.clientWidth || window.innerWidth

  // Slide one full panel so the neighbour reaches centre, then swap it in as the
  // current item and recentre in the same paint — the panel slid to and the new
  // centre panel are identical, so the transition is seamless. Shared by every
  // navigation entry point.
  const commitNavigate = (dir: -1 | 1) => {
    committing.current = true
    const target = dir === 1 ? -panelWidth() : panelWidth()
    setAnimating(true)
    setDragX(target)
    if (commitTimer.current !== null) clearTimeout(commitTimer.current)
    commitTimer.current = setTimeout(() => {
      onNavigate(dir)
      setAnimating(false)
      setDragX(0)
      committing.current = false
    }, SWIPE_COMMIT_MS)
  }

  // Button / keyboard entry point: animate from centre if allowed.
  const animateNavigate = (dir: -1 | 1) => {
    if (committing.current || !canNavigate(dir)) return
    commitNavigate(dir)
  }
  // Stable handle so the keydown listener always calls the latest navigator.
  const animateNavigateRef = useRef(animateNavigate)
  animateNavigateRef.current = animateNavigate

  const onTouchStart = (e: React.TouchEvent) => {
    if (committing.current) return
    const t = e.touches[0]
    gesture.current = { x: t.clientX, y: t.clientY, axis: null }
    setAnimating(false)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    const g = gesture.current
    if (g === null || committing.current) return
    const t = e.touches[0]
    const dx = t.clientX - g.x
    const dy = t.clientY - g.y
    if (g.axis === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
      g.axis = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
    }
    if (g.axis === 'v') return // let the content scroll vertically
    // Add resistance when the swipe would run past the first/last item.
    const screenSign: -1 | 1 = dx < 0 ? -1 : 1
    const resisted = canNavigate(navDirFor(screenSign)) ? dx : dx * 0.3
    setDragX(resisted)
  }

  const onTouchEnd = () => {
    const g = gesture.current
    gesture.current = null
    if (g === null || g.axis !== 'h' || committing.current) { setDragX(0); return }
    const screenSign: -1 | 1 = dragX < 0 ? -1 : 1
    const dir = navDirFor(screenSign)
    if (Math.abs(dragX) <= SWIPE_THRESHOLD || !canNavigate(dir)) {
      // Snap back to centre, then drop the transition flag so the neighbour
      // panels unmount once settled.
      setAnimating(true)
      setDragX(0)
      commitTimer.current = setTimeout(() => setAnimating(false), SWIPE_COMMIT_MS)
      return
    }
    // Continue the finger's drag into the seamless commit slide.
    commitNavigate(dir)
  }

  // Closed position is off the inline-end edge: right in LTR, left in RTL.
  const closedTransform = isRtl ? '-translate-x-full' : 'translate-x-full'

  // The neighbour panels are heavier (markdown, etc.), so only mount them while
  // a swipe is actually in flight; the centre panel is always present.
  const showNeighbours = animating || dragX !== 0

  // Plain function (NOT a component) so each slot reconciles by position and the
  // centre panel never remounts as dragX changes during a swipe.
  const renderSlot = (panel: DrawerItem | null) => (
    <div className="flex h-full w-full flex-col">
      {panel === null ? null
        : panel.kind === 'challenge' ? <ChallengeDetail challenge={panel.challenge} onStart={handleStart} />
        : panel.kind === 'assignment' ? <AssignmentDetail assignment={panel.assignment} onStart={handleStart} />
        : (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <p className="text-lg font-medium text-foreground">
              {panel.authenticated ? t('challenges.unavailableMessage') : t('challenges.unavailableLoginTitle')}
            </p>
            {!panel.authenticated && (
              <Button onClick={() => onLogin?.()}>{t('challenges.unavailableLoginCta')}</Button>
            )}
          </div>
        )}
    </div>
  )

  return (
    <>
      <div onClick={onClose} aria-hidden className={`fixed inset-0 z-40 bg-black/60 transition-opacity duration-300 ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`} />
      <aside role="dialog" aria-modal={open} aria-label={rendered === null ? '' : rendered.kind === 'unavailable' ? (rendered.authenticated ? t('challenges.unavailableMessage') : t('challenges.unavailableLoginTitle')) : itemName(rendered)} aria-hidden={!open}
        className={`fixed inset-y-0 end-0 z-50 flex w-full flex-col bg-card shadow-2xl transition-transform duration-300 ease-out sm:w-[min(490px,94vw)] sm:border-s sm:border-border ${open ? 'translate-x-0' : closedTransform}`}>
        {rendered !== null && (
          <>
            <div className="flex items-center gap-2 border-b border-border p-3">
              {rendered.kind !== 'unavailable' && (
                <>
                  <button type="button" aria-label={t('challenges.previous')} disabled={position <= 1} onClick={() => animateNavigate(-1)}
                    className="grid h-8 w-8 place-items-center rounded-lg border border-border disabled:opacity-40">‹</button>
                  <button type="button" aria-label={t('challenges.next')} disabled={position >= total} onClick={() => animateNavigate(1)}
                    className="grid h-8 w-8 place-items-center rounded-lg border border-border disabled:opacity-40">›</button>
                  <span className="font-mono text-xs text-muted-foreground">{position} / {total}</span>
                </>
              )}
              <button type="button" aria-label={t('common.close')} onClick={onClose} className="ms-auto grid h-8 w-8 place-items-center rounded-lg border border-border">✕</button>
            </div>
            {/* Swipe surface: [prev][current][next] absolutely stacked, the prev
                pinned one width to the physical left and next one width to the
                right. A moving layer follows the finger; dragging reveals the
                real neighbour with no gap, and a commit slides one width and
                recentres. Physical (px) translates keep it RTL-independent. */}
            <div
              ref={trackRef}
              className="relative min-h-0 flex-1 overflow-hidden"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div
                className={`absolute inset-0 ${animating ? 'transition-transform duration-200 ease-out' : ''}`}
                style={{ transform: `translateX(${dragX}px)` }}
              >
                <div className="absolute inset-0" style={{ transform: 'translateX(-100%)' }}>
                  {renderSlot(showNeighbours ? prevItem : null)}
                </div>
                {/* Read `item` directly so a committed swap shows the new item in
                    the same paint; fall back to the retained item only while the
                    drawer is closing (item briefly null). */}
                <div className="absolute inset-0">
                  {renderSlot(item ?? rendered)}
                </div>
                <div className="absolute inset-0" style={{ transform: 'translateX(100%)' }}>
                  {renderSlot(showNeighbours ? nextItem : null)}
                </div>
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  )
}
